import { Product } from "@/api/product-api";
import { CurrencyIsoCode } from "@/enums";
import {
  getSetDeal,
  SET_VARIANT_BY_SLUG,
  SetDeal,
} from "@/libs/product/set-variant";
import { ProductUtil } from "@/utils/ProductUtil";

type Line = { slug: string; sku: string; quantity: number };

/**
 * **세트로 묶어도 되는가** — 묶기(`packSetLines`)와 표시(`useSetDeal`)가 공유하는 단일 술어.
 *
 * 셋 다 성립해야 한다:
 *   - 세트·단품 둘 다 이 통화·티어 가격이 있다(`getProductPrice` 가 null 이 아님)
 *   - 세트 재고 > 0
 *   - 세트 실판매가 < 단품 실판매가 × units — **싸지 않으면 묶지 않는다.** 묶으면 「할인」
 *     이라는 이름으로 더 비싸게 청구된다(가격 등록 실수 한 번이면 난다)
 *
 * 표시가 따로 판정하면 화면은 단품가인데 결제는 세트가(또는 반대)로 갈린다.
 */
export function canPackSet(
  deal: SetDeal,
  {
    currency,
    countryCode,
  }: { currency: CurrencyIsoCode; countryCode: string | null },
): boolean {
  if ((deal.set.skuOptions[0].stock ?? 0) <= 0) return false;
  const priceOf = (product: Product) =>
    ProductUtil.getProductPrice({ currency, countryCode, product });
  const base = priceOf(deal.base);
  const set = priceOf(deal.set);
  if (!base || !set) return false;
  const sale = (p: { originalAmount: number; discountAmount: number }) =>
    p.originalAmount - p.discountAmount;
  return sale(set) < sale(base) * deal.units;
}

/**
 * **단품 N개 → 세트 ⌊N/units⌋ + 단품 N%units** 로 라인을 다시 쓴다 (MOLLE Pouch ×2 등).
 *
 * 세트·단품 라인이 섞여 들어와도 **단품 총량**으로 환산한 뒤 다시 나눈다 — 그래서 어느 쪽이
 * 몇 줄 있든, 몇 번 호출하든 결과가 같다(멱등). 카트 쓰기 경로와 PDP 결제 경로가 전부 이
 * 함수를 지나야 「2개 사면 세트가」 약속이 경로마다 갈리지 않는다:
 *
 *   카트   `useAddItem` · `useAddDevice` · `useAddAccessories` · `useAddCartStoreItem` ·
 *          `useIncreaseItem` (`store/cart-store/index.ts`)
 *   PDP    `useBuyTactSuitCheckout`(바로 결제) · `buildAccessoryNaverPayItems`(네이버페이)
 *          — 카트에 담기(`addAccessoriesToCart`)는 `useAddAccessories` 가 받는다
 *
 * ⚠️ **묶을 수 없으면 묶지 않고, 이미 묶인 세트는 단품 × units 로 푼다**(`canPackSet` — 가격
 * 없음 · 세트 재고 0 · 세트가 안 쌈). 가격 없는 세트로 두면 카트가 「이용 불가 항목」으로 결제를
 * 막고, 안 싼 세트로 두면 「할인」이라는 이름으로 더 비싸게 청구된다. 풀면 단품가로 살 수 있다
 * — 할인을 잃는 쪽이 구매를 잃는 쪽보다 낫다.
 *
 * ★**푸는 판정은 쓰기 시점만으로는 부족하다** (#5906 R1 1번). 세트를 담은 뒤 가격·재고가
 * 바뀌어도 카트에 다시 쓰지 않으면 이 함수가 안 불린다(카트는 persist 되어 며칠 뒤 결제될 수
 * 있다). 그래서 카트는 `useNormalizeCartSets`(`_app` 의 `CartSetNormalizer`)가 카탈로그·통화·
 * 국가·카트가 바뀔 때마다 이 함수를 다시 지나게 한다 — **결제 직전이 아니라 화면에 보일 때**
 * 고친다. 결제 직전에만 고치면 사용자가 본 금액과 청구가 확인 없이 갈린다.
 *
 * ⚠️ 짝을 검증할 수 없으면(`getSetDeal` null — 세트가 카탈로그에서 빠짐 등) 단품 몇 개인지
 * 몰라 **풀지 않는다**. 그 세트 줄은 카트의 「이용 불가 항목」 경로가 받는다.
 *
 * ⚠️ 라인의 **첫 등장 위치**를 지킨다. `useBuyTactSuitCheckout` 은 배열 순서에 의미를 둔다
 * (*"accessory 가 먼저 추가되어야 0번 아이템이 product 가 됨"*).
 */
export function packSetLines<T extends Line>(
  lines: T[],
  products: Product[],
  {
    currency,
    countryCode,
  }: { currency: CurrencyIsoCode; countryCode: string | null },
): T[] {
  let result = lines;

  for (const baseSlug of Object.keys(SET_VARIANT_BY_SLUG)) {
    const deal = getSetDeal(products, baseSlug);
    if (!deal) continue;

    const isBase = (l: Line) => l.sku === deal.baseSku;
    const isSet = (l: Line) => l.sku === deal.setSku;
    const firstIndex = result.findIndex((l) => isBase(l) || isSet(l));
    if (firstIndex === -1) continue;

    // 묶을 수 없을 때 — 세트 줄이 없으면 손대지 않고(순서·줄 수 보존), 있으면 단품으로 푼다.
    const packable = canPackSet(deal, { currency, countryCode });
    if (!packable && !result.some(isSet)) continue;

    const totalUnits = result.reduce(
      (sum, l) =>
        sum + (isBase(l) ? l.quantity : isSet(l) ? l.quantity * deal.units : 0),
      0,
    );
    const setQty = packable ? Math.floor(totalUnits / deal.units) : 0;
    const baseQty = packable ? totalUnits % deal.units : totalUnits;

    // 템플릿은 원래 라인을 그대로 쓴다 — 호출처마다 실린 필드(`options` 등)를 잃지 않게.
    const template = result[firstIndex];
    const packed: T[] = [];
    if (setQty > 0) {
      packed.push({
        ...template,
        slug: deal.set.slug,
        sku: deal.setSku,
        quantity: setQty,
      });
    }
    if (baseQty > 0) {
      packed.push({
        ...template,
        slug: deal.base.slug,
        sku: deal.baseSku,
        quantity: baseQty,
      });
    }

    const rest = result.filter((l) => !isBase(l) && !isSet(l));
    const insertAt = result
      .slice(0, firstIndex)
      .filter((l) => !isBase(l) && !isSet(l)).length;
    result = [...rest.slice(0, insertAt), ...packed, ...rest.slice(insertAt)];
  }

  return result;
}
